import React from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiOpenInNew } from "@mdi/js";
import stripeWordmark from "app/images/stripe-wordmark.svg";
import "./paymentConnection.css";
import {
  PC_STATE,
  STATE_META,
  PC_TITLE,
  PC_SUBTITLE,
  PC_FEE_DISCLAIMER,
  NOT_CONNECTED_TEXT,
  NO_COMPANY_TEXT,
  DETAIL_PLACEHOLDER,
} from "./constants";

const Header = () => (
  <div>
    <h2 className="mb-2">{PC_TITLE}</h2>
    <p className="mb-1">{PC_SUBTITLE}</p>
    <p className="small text-muted mb-0">{PC_FEE_DISCLAIMER}</p>
  </div>
);

const PoweredByStripe = () => (
  <div className="d-flex align-items-center gap-1 flex-shrink-0">
    <span className="text-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
      Powered by
    </span>
    <img
      src={stripeWordmark}
      alt="Stripe"
      style={{ width: "37.33px", height: 16, display: "block" }}
    />
  </div>
);

const DetailRow = ({ label, value, isPlaceholder }) => (
  <div className="d-flex">
    <span className="text-muted flex-shrink-0" style={{ width: 140 }}>
      {label}
    </span>
    <span className={isPlaceholder ? "text-muted" : undefined}>{value}</span>
  </div>
);

const DetailsBox = ({ account, isIncomplete }) => {
  const business = account?.business_name;
  const bank = account?.payout_bank_name
    ? `${account.payout_bank_name} **** ${account.payout_bank_last4 || ""}`.trim()
    : null;
  const fallback = isIncomplete ? DETAIL_PLACEHOLDER : "—";

  return (
    <div
      className="bg-white rounded-3 p-3 d-flex justify-content-between align-items-end"
      style={{ gap: 16 }}
    >
      <div className="d-flex flex-column gap-2">
        <DetailRow
          label="Business"
          value={business || fallback}
          isPlaceholder={!business}
        />
        <DetailRow
          label="Payout Bank"
          value={bank || fallback}
          isPlaceholder={!bank}
        />
      </div>
      <PoweredByStripe />
    </div>
  );
};

const ExternalLabel = ({ children }) => (
  <>
    {children}
    <Icon path={mdiOpenInNew} size={0.75} />
  </>
);

const buildActions = (state, h) => {
  switch (state) {
    case PC_STATE.INCOMPLETE:
      return {
        left: { label: "Cancel Payment Setup", onClick: h.onCancel },
        right: [
          { label: "How it works", variant: "outline-primary", onClick: h.onHowItWorks },
          { label: "Continue Payment Setup", variant: "primary", onClick: h.onContinue },
        ],
      };
    case PC_STATE.PENDING:
      return {
        left: { label: "Remove Payment Setup", onClick: h.onRemove },
        right: [
          { label: "How it works", variant: "outline-primary", onClick: h.onHowItWorks },
        ],
      };
    case PC_STATE.CONNECTED:
      return {
        left: { label: "Disable Payments", onClick: h.onDisable },
        right: [
          { label: "How it works", variant: "outline-primary", onClick: h.onHowItWorks },
          {
            label: <ExternalLabel>Manage Payments</ExternalLabel>,
            variant: "primary",
            onClick: h.onManage,
          },
        ],
      };
    case PC_STATE.DISABLED:
      return {
        left: { label: "Remove Payment Setup", onClick: h.onRemove },
        right: [
          { label: "How it works", variant: "outline-primary", onClick: h.onHowItWorks },
          { label: "Re-enable Payments", variant: "primary", onClick: h.onReenable },
        ],
      };
    case PC_STATE.ERROR:
      return {
        left: { label: "Remove Payment Setup", onClick: h.onRemove },
        right: [
          {
            label: <ExternalLabel>View Account Issue</ExternalLabel>,
            variant: "danger",
            onClick: h.onViewIssue,
          },
        ],
      };
    default:
      return { left: null, right: [] };
  }
};

const CardShell = ({ children }) => (
  <Card className="pc-card">
    <Card.Body className="d-flex flex-column" style={{ gap: "2rem" }}>
      <Header />
      {children}
    </Card.Body>
  </Card>
);

const PaymentConnectionCard = ({ state, account, actionLoading, handlers }) => {
  if (state === PC_STATE.LOADING) {
    return (
      <CardShell>
        <div className="d-flex align-items-center gap-2 text-muted">
          <Spinner as="span" animation="border" size="sm" /> Loading payment status…
        </div>
      </CardShell>
    );
  }
  if (state === PC_STATE.ERROR_FETCH) {
    return (
      <CardShell>
        <p className="text-muted mb-0">
          We couldn't load your payment status. Please refresh and try again.
        </p>
      </CardShell>
    );
  }
  // No Company on the account. Distinct from ERROR_FETCH: nothing is broken and
  // refreshing alone will not help -- the user has to create the Company first.
  if (state === PC_STATE.NO_COMPANY) {
    return (
      <CardShell>
        <p className="text-muted mb-0">{NO_COMPANY_TEXT}</p>
      </CardShell>
    );
  }

  if (state === PC_STATE.NOT_CONNECTED) {
    return (
      <CardShell>
        <div className="d-flex flex-column align-items-center gap-3 text-center">
          <p className="text-muted mb-0">{NOT_CONNECTED_TEXT}</p>
          <div className="d-flex flex-column align-items-center gap-3">
            <Button
              variant="primary"
              style={{ width: 240 }}
              onClick={handlers.onSetUp}
              disabled={actionLoading}
            >
              Set Up Payment Account
            </Button>
            <Button
              variant="outline-primary"
              style={{ width: 240 }}
              onClick={handlers.onHowItWorks}
            >
              How it works
            </Button>
          </div>
        </div>
      </CardShell>
    );
  }

  const meta = STATE_META[state];
  const actions = buildActions(state, handlers);

  return (
    <CardShell>
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center gap-2">
          <span
            className="flex-shrink-0"
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: meta.dotColor,
            }}
          />
          <span className="small text-muted">{meta.label}</span>
        </div>
        {meta.body && <p className="mb-0">{meta.body}</p>}
        <DetailsBox account={account} isIncomplete={state === PC_STATE.INCOMPLETE} />
      </div>

      <div className="d-flex justify-content-between align-items-center">
        {actions.left ? (
          <Button
            variant="link"
            className="text-secondary text-decoration-none px-0"
            onClick={actions.left.onClick}
            disabled={actionLoading}
          >
            {actions.left.label}
          </Button>
        ) : (
          <span />
        )}
        <div className="d-flex align-items-center gap-2">
          {actions.right.map((btn, i) => (
            <Button
              key={i}
              variant={btn.variant}
              onClick={btn.onClick}
              disabled={actionLoading}
              className="d-inline-flex align-items-center gap-2"
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
    </CardShell>
  );
};

export default PaymentConnectionCard;
